/**
 * Pure mappers of the AIRA conversation layer — no Vue, no network.
 * Specs run on `fixtures/` (the real vocab and socket event, the rest built
 * from the backend entities — see docs/AIRA_FE_DIAGNOSIS.md § 3).
 */

import type {
  AiraActionRequest,
  AiraAction,
  AiraAskAction,
  AiraButtonsAction,
  AiraCapsuleReadyAction,
  AiraCommand,
  AiraNavigateCommand,
  AiraAudience,
  AiraItemSource,
  AiraCanvasItemRef,
  AiraContextEnvelope,
  AiraMessage,
  AiraMessageStatus,
  AiraModifyOutfitAction,
  AiraProposeOutfitsAction,
  AiraSendMessageResponse,
  AiraSocketEvent,
  AiraSurface,
  AiraVocabEntry,
  AiraWarnAction,
} from './types'

export const AIRA_MAX_CANVAS_ITEMS = 20

/** The backend's model timeout is 30 s; the UI gives it 2 s more before a local `degraded`. */
export const AIRA_UI_TIMEOUT_MS = 32_000

/** A repeat with the same id while the first call runs answers `pending`; ask again after this. */
export const AIRA_PENDING_RETRY_MS = 2_000

/* ——— delivery state on top of the wire message ——— */

export type AiraDelivery = 'sending' | 'sent' | 'answered' | 'degraded' | 'failed'

/** Why the client marked a turn degraded / failed without a backend reply. */
export type AiraLocalReason = 'timeout' | 'network' | 'rate_limited' | 'rejected'

export interface AiraChatMessage extends AiraMessage {
  /** Stable render key: the client id when there is one, else the server id. */
  key: string
  delivery: AiraDelivery
  retryable: boolean
  /** The line exists only on this client (optimistic user line, local degraded reply). */
  local?: boolean
  localReason?: AiraLocalReason
  retryAfter?: number | null
  /** A Shuffle / Replace line: what `retry` re-posts (the text is only the label). */
  action?: AiraActionRequest
}

export function newAiraClientMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/** User and reply share the client id; the key tells them apart by role. */
export function airaMessageKey(message: Pick<AiraMessage, 'id' | 'role' | 'clientMessageId'>): string {
  if (message.clientMessageId) {
    return `${message.role === 'user' ? 'u' : 'a'}:${message.clientMessageId}`
  }
  return `m:${message.id}`
}

export function airaDeliveryOf(status: AiraMessageStatus): AiraDelivery {
  if (status === 'degraded') return 'degraded'
  if (status === 'pending') return 'sent'
  return 'answered'
}

export function toAiraChatMessage(message: AiraMessage): AiraChatMessage {
  return {
    ...message,
    key: airaMessageKey(message),
    delivery: airaDeliveryOf(message.status),
    retryable: message.status === 'degraded',
  }
}

/** Ascending by time, then id — the API pages newest first, the log reads oldest first. */
export function sortAiraMessages(messages: readonly AiraChatMessage[]): AiraChatMessage[] {
  return [...messages].sort((a, b) => {
    const byTime = Date.parse(a.createdAt) - Date.parse(b.createdAt)
    if (byTime !== 0 && !Number.isNaN(byTime)) return byTime
    return a.id - b.id
  })
}

/**
 * Merge a page (or a reconnect re-read) into the log without duplicates: the
 * server row wins over a local one with the same key; a local line that the
 * server does not know yet (still sending) stays.
 */
export function mergeAiraMessages(
  existing: readonly AiraChatMessage[],
  incoming: readonly AiraMessage[],
): AiraChatMessage[] {
  const byKey = new Map(existing.map((message) => [message.key, message]))
  for (const raw of incoming) {
    const next = toAiraChatMessage(raw)
    byKey.set(next.key, next)
  }
  return sortAiraMessages([...byKey.values()])
}

/* ——— envelope ——— */

export interface AiraEnvelopeInput {
  surface: AiraSurface
  canvasItems?: ReadonlyArray<{ productId?: number | null; overviewId?: number | null }>
  occasion?: string | null
  season?: string | null
  style?: string | null
  outfitId?: number | null
  capsuleId?: number | null
  audience?: AiraAudience | null
  /** Pinned pieces — only those also on the canvas are sent. */
  keep?: ReadonlyArray<{ productId?: number | null; overviewId?: number | null }>
  focusItem?: { productId?: number | null; overviewId?: number | null } | null
  itemSource?: AiraItemSource | null
  answers?: Record<string, string | string[]> | null
}

type AiraPieceRef = { productId?: number | null; overviewId?: number | null }

function airaCanvasRefOf(item: AiraPieceRef): AiraCanvasItemRef | null {
  return item.overviewId != null
    ? { wardrobeProductOverviewId: item.overviewId }
    : item.productId != null
      ? { productId: item.productId }
      : null
}

function airaCanvasRefKey(ref: AiraCanvasItemRef): string {
  return ref.wardrobeProductOverviewId != null ? `w:${ref.wardrobeProductOverviewId}` : `p:${ref.productId}`
}

/** Only what is on the screen, ids only, at most 20 pieces, no piece twice. */
export function buildAiraEnvelope(input: AiraEnvelopeInput): AiraContextEnvelope {
  const canvasItems: AiraCanvasItemRef[] = []
  const seen = new Set<string>()
  for (const item of input.canvasItems ?? []) {
    const ref = airaCanvasRefOf(item)
    if (!ref) continue
    const id = airaCanvasRefKey(ref)
    if (seen.has(id)) continue
    seen.add(id)
    canvasItems.push(ref)
    if (canvasItems.length >= AIRA_MAX_CANVAS_ITEMS) break
  }
  const envelope: AiraContextEnvelope = { surface: input.surface, canvasItems }
  if (input.occasion) envelope.occasion = input.occasion
  if (input.season) envelope.season = input.season
  if (input.style) envelope.style = input.style
  if (input.outfitId != null) envelope.outfitId = input.outfitId
  if (input.capsuleId != null) envelope.capsuleId = input.capsuleId
  if (input.audience) envelope.audience = input.audience
  if (input.keep?.length) {
    // `keep` ⊆ `canvasItems`: a pinned piece that left the canvas is not a constraint
    const keep: AiraCanvasItemRef[] = []
    const kept = new Set<string>()
    for (const item of input.keep) {
      const ref = airaCanvasRefOf(item)
      if (!ref) continue
      const id = airaCanvasRefKey(ref)
      if (!seen.has(id) || kept.has(id)) continue
      kept.add(id)
      keep.push(ref)
    }
    if (keep.length) envelope.keep = keep
  }
  if (input.focusItem) {
    const ref = airaCanvasRefOf(input.focusItem)
    if (ref && seen.has(airaCanvasRefKey(ref))) envelope.focusItem = ref
  }
  if (input.itemSource) envelope.itemSource = input.itemSource
  if (input.answers && Object.keys(input.answers).length) envelope.answers = { ...input.answers }
  return envelope
}

export function sameAiraCanvasItem(a: AiraCanvasItemRef, b: AiraCanvasItemRef): boolean {
  if (a.wardrobeProductOverviewId != null || b.wardrobeProductOverviewId != null) {
    return a.wardrobeProductOverviewId === b.wardrobeProductOverviewId
  }
  return a.productId != null && a.productId === b.productId
}

/* ——— socket turn → the REST result shape, so the store has one path ——— */

export function airaSocketTurnToResponse(
  event: Extract<AiraSocketEvent, { type: 'message' | 'degraded' }>,
  userMessage: AiraChatMessage,
  now: string = new Date().toISOString(),
): AiraSendMessageResponse {
  const status: AiraMessageStatus = event.type === 'degraded' ? 'degraded' : event.status
  const assistantMessage: AiraMessage = {
    id: event.messageId ?? 0,
    threadId: event.threadId,
    role: 'aira',
    text: event.content,
    surface: userMessage.surface,
    status,
    context: null,
    actions: event.actions,
    clientMessageId: event.clientMessageId,
    mlRequestId: null,
    lang: userMessage.lang,
    createdAt: now,
  }
  return {
    threadId: event.threadId,
    message: { ...userMessage, threadId: event.threadId ?? userMessage.threadId, status: 'ok' },
    assistantMessage: status === 'pending' ? null : assistantMessage,
    actions: event.actions,
    status,
    retryable: event.retryable || status === 'degraded',
    intent: event.intent,
    latencyMs: event.latencyMs,
  }
}

/* ——— actions ——— */

export function airaAskOf(message: Pick<AiraMessage, 'actions'>): AiraAskAction | null {
  return (message.actions.find((action) => action.type === 'ask') as AiraAskAction | undefined) ?? null
}

export function airaCapsuleReadyOf(message: Pick<AiraMessage, 'actions'>): AiraCapsuleReadyAction | null {
  return (message.actions.find((action) => action.type === 'capsule_ready') as AiraCapsuleReadyAction | undefined) ?? null
}

export function airaButtonsOf(message: Pick<AiraMessage, 'actions'>): AiraButtonsAction | null {
  return (message.actions.find((action) => action.type === 'buttons') as AiraButtonsAction | undefined) ?? null
}

/** The commands of a reply, in order — everything the canvas executes rather than renders. */
export function airaCommandsOf(message: Pick<AiraMessage, 'actions'>): AiraCommand[] {
  return message.actions.filter(
    (action): action is Exclude<AiraCommand, AiraNavigateCommand> =>
      action.type === 'set_occasion' ||
      action.type === 'pin' ||
      action.type === 'unpin' ||
      action.type === 'regenerate' ||
      action.type === 'open_product' ||
      action.type === 'style_product',
  )
}

export function airaProposeOutfitsOf(message: Pick<AiraMessage, 'actions'>): AiraProposeOutfitsAction | null {
  return (message.actions.find((action) => action.type === 'propose_outfits') as AiraProposeOutfitsAction | undefined) ?? null
}

export function airaModifyOutfitOf(message: Pick<AiraMessage, 'actions'>): AiraModifyOutfitAction | null {
  return (message.actions.find((action) => action.type === 'modify_outfit') as AiraModifyOutfitAction | undefined) ?? null
}

export function airaWarnOf(message: Pick<AiraMessage, 'actions'>): AiraWarnAction | null {
  return (message.actions.find((action) => action.type === 'warn') as AiraWarnAction | undefined) ?? null
}

export function isAiraActionOfType<T extends AiraAction['type']>(
  action: AiraAction,
  type: T,
): action is Extract<AiraAction, { type: T }> {
  return action.type === type
}

/* ——— vocabulary ——— */

/** Exact code first, then a case-insensitive label match; null when the word is not in the list. */
export function findAiraVocabEntry(
  entries: readonly AiraVocabEntry[] | null | undefined,
  value: string | null | undefined,
): AiraVocabEntry | null {
  if (!entries || !value) return null
  const needle = value.trim().toLowerCase()
  if (!needle) return null
  return (
    entries.find((entry) => entry.code.toLowerCase() === needle)
    ?? entries.find((entry) => entry.label.toLowerCase() === needle)
    ?? null
  )
}

const SEASON_BY_MONTH: readonly string[] = [
  'winter', 'winter', 'spring', 'spring', 'spring', 'summer',
  'summer', 'summer', 'autumn', 'autumn', 'autumn', 'winter',
]

/** The season the calendar suggests (northern hemisphere), only when the vocab knows it. */
export function airaSeasonCodeForMonth(
  month: number,
  seasons: readonly AiraVocabEntry[] | null | undefined,
): string | null {
  const guess = SEASON_BY_MONTH[((month % 12) + 12) % 12]
  return findAiraVocabEntry(seasons, guess)?.code ?? null
}

export function nextAiraVocabCode(entries: readonly AiraVocabEntry[], current: string | null): string | null {
  if (!entries.length) return null
  const index = entries.findIndex((entry) => entry.code === current)
  return entries[(index + 1) % entries.length]?.code ?? null
}
